import { Toast } from '@gafe/common-ui';
import { AxiosResponse } from 'axios';
import find from 'lodash/find';
import { put, select, takeEvery } from 'redux-saga/effects';

import {
  Activity,
  ActivityController,
  fetchActivityDetailsData,
  InfoToFetchActivityData,
  refetchActivities,
} from '@/controllers/Activity';
import { DictionaryController } from '@/controllers/Dictionary';
import { GoogleAnalytics } from '@/services/Analytics';
import { ActivitiesActionTypes } from '@/store/reducers/activities/action-types';
import { DictionariesActionTypes } from '@/store/reducers/dictionaries/action-types';
import { selectOpportunityStages } from '@/store/reducers/dictionaries/selectors';
import { openSubmitOrderModal } from '@/store/reducers/global/actions';
import { OpportunitiesActionTypes } from '@/store/reducers/opportunities/action-types';
import { ReduxAction } from '@/types';

import { OpportunityController } from './OpportunityController';
import { AcceptProposalInfo, ChangeStageInfo, CloseLostInfo, StageCode } from './types';

function* changeStage(action: ReduxAction) {
  type Payload = {
    changeStageInfo: ChangeStageInfo;
    activityData?: InfoToFetchActivityData;
    openSubmitModalOnSuccess: boolean;
  };

  const { changeStageInfo, activityData, openSubmitModalOnSuccess } = action.payload as Payload;
  const { opportunityId, stageCode, ...additionalInfo } = changeStageInfo;

  try {
    if (stageCode === StageCode.RequestNewProposal) {
      yield OpportunityController.changeStage(opportunityId, additionalInfo);
    } else {
      yield OpportunityController.changeStage(opportunityId, {
        ...additionalInfo,
        stage: yield getStageId(stageCode),
        isRequestingNewProposal: false,
      });
    }

    // Re-fetch data, that is visible on the page, so it will be fresh and "up to date"
    const activities = yield refetchActivities();

    // Activity data is passed if we need to update details side page
    if (activityData) yield updateDetailsSidePage(activityData, activities);

    yield put({ type: OpportunitiesActionTypes.CHANGE_STAGE.SUCCESS });

    if (stageCode === StageCode.ClosedLost) {
      GoogleAnalytics.closeProposalForOpportunityAsLost(
        opportunityId,
        (additionalInfo as CloseLostInfo).syncedProposalId,
      );
    }

    if (opportunityId && stageCode === StageCode.ProposalAccepted) {
      GoogleAnalytics.acceptProposalForOpportunity(
        opportunityId,
        (additionalInfo as AcceptProposalInfo).syncedProposalId,
      );
      if (openSubmitModalOnSuccess) {
        yield put(openSubmitOrderModal(opportunityId, Boolean(activityData)));
      }
    }

    if (stageCode === StageCode.RequestNewProposal) {
      Toast({
        message: 'Your new proposal request was successfully submitted.',
        title: 'New proposal requested',
        type: 'success',
      });
      GoogleAnalytics.requestNewProposalForOpportunity(opportunityId);
    }
  } catch (error: GlobalAny) {
    yield put({ type: OpportunitiesActionTypes.CHANGE_STAGE.FAIL, error });
    console.error(error);

    Toast({
      message: 'Something went wrong, please contact your project coordinator.',
      title: 'Operation failed',
      type: 'destructive',
    });
  }
}

function* getStageId(stageCode: string) {
  let stages = yield select(selectOpportunityStages);

  if (stages.length === 0) {
    const dictionaryName = 'opportunityStageType';
    const stagesResponse = yield DictionaryController.fetchOpportunityDictionary(dictionaryName);

    stages = (stagesResponse as AxiosResponse).data;
  }

  yield put({
    type: DictionariesActionTypes.FETCH_OPPORTUNITY_STAGES.SUCCESS,
    sagaPayload: { dictionaryName: 'opportunityStages' },
    payload: stages,
  });

  return find(stages, { code: stageCode })?.id;
}

export function* updateDetailsSidePage(
  activityData: InfoToFetchActivityData,
  activities: Activity[],
) {
  let detailsTargetActivity = find(activities, { id: activityData.activityId });

  // If there is no activity in re-fetched activities, fetch activity by id
  if (!detailsTargetActivity) {
    const activityResponse = yield ActivityController.fetchActivity(activityData.activityId);

    detailsTargetActivity = (activityResponse as AxiosResponse).data;
  }

  yield put({
    type: ActivitiesActionTypes.SAVE_DETAILS_TARGET_ACTIVITY,
    payload: detailsTargetActivity,
  });

  yield fetchActivityDetailsData({ type: '', sagaPayload: activityData });
}

function* assignSalesRepresentative(data: ReduxAction) {
  try {
    yield OpportunityController.assignSalesRepresentative(data.payload);
    yield put({ type: OpportunitiesActionTypes.ASSIGN_SALES_REPRESENTATIVE.SUCCESS });
  } catch {
    yield put({ type: OpportunitiesActionTypes.ASSIGN_SALES_REPRESENTATIVE.FAIL });
  }
}

export function* OpportunityControllerSaga() {
  yield takeEvery(
    OpportunitiesActionTypes.ASSIGN_SALES_REPRESENTATIVE.START,
    assignSalesRepresentative,
  );
  yield takeEvery(OpportunitiesActionTypes.CHANGE_STAGE.START, changeStage);
}
